import React, { useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import TuneIcon from "@mui/icons-material/Tune";
import { alpha, useTheme } from "@mui/material/styles";
import {
  BAND_OPTIONS,
  RECOMMENDED_ACTION_OPTIONS,
  RECOMMENDATION_STATUS_OPTIONS,
  STATUS_OPTIONS,
} from "../report.utils";

function SelectField({ label, value, onChange, options, fullWidth = true }) {
  return (
    <TextField
      select
      label={label}
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      fullWidth={fullWidth}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: "8px",
        },
      }}
    >
      {options.map((opt) => (
        <MenuItem key={String(opt.value)} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

function lookupToOptions(rows = [], defaultLabel = "All") {
  const seen = new Set();
  const options = [{ value: "", label: defaultLabel }];

  rows.forEach((row) => {
    const value = row?.id ?? row?.name;
    const label = row?.name ?? row?.label ?? row?.id;
    if (value === null || value === undefined || label === null || label === undefined) return;
    const key = String(value);
    if (seen.has(key)) return;
    seen.add(key);
    options.push({ value: String(value), label: String(label) });
  });

  return options;
}

export default function ReportFilters({
  filters,
  search,
  lookups,
  onFilterChange,
  onSearchChange,
  onReset,
  onExportCsv,
  onExportExcel,
  onFinalize,
  canFinalize,
  finalizeDisabled,
  selectedCount,
}) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  const cycleOptions = useMemo(
    () => lookupToOptions(lookups?.cycles, "All cycles"),
    [lookups?.cycles]
  );
  const departmentOptions = useMemo(
    () => lookupToOptions(lookups?.departments, "All departments"),
    [lookups?.departments]
  );
  const projectOptions = useMemo(
    () => lookupToOptions(lookups?.projects, "All projects"),
    [lookups?.projects]
  );

  const filtersContent = (
    <Stack spacing={1.25}>
      <Box
        sx={{
          display: "grid",
          gap: 1,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
            xl: "repeat(5, minmax(0, 1fr))",
          },
          "& .MuiTextField-root": { width: "100%" },
        }}
      >
        <TextField
          type="date"
          size="small"
          label="From"
          value={filters.from || ""}
          onChange={(e) => onFilterChange("from", e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ "aria-label": "From date" }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
        />

        <TextField
          type="date"
          size="small"
          label="To"
          value={filters.to || ""}
          onChange={(e) => onFilterChange("to", e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ "aria-label": "To date" }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
        />

        <SelectField
          label="Cycle"
          value={filters.cycle_id || ""}
          onChange={(v) => onFilterChange("cycle_id", v)}
          options={cycleOptions}
        />

        <SelectField
          label="Department"
          value={filters.department_id || ""}
          onChange={(v) => onFilterChange("department_id", v)}
          options={departmentOptions}
        />

        <SelectField
          label="Project"
          value={filters.project_id || ""}
          onChange={(v) => onFilterChange("project_id", v)}
          options={projectOptions}
        />

        <SelectField
          label="Status"
          value={filters.status || ""}
          onChange={(v) => onFilterChange("status", v)}
          options={STATUS_OPTIONS}
        />

        <SelectField
          label="Band"
          value={filters.band || ""}
          onChange={(v) => onFilterChange("band", v)}
          options={BAND_OPTIONS}
        />

        <SelectField
          label="Recommended Action"
          value={filters.recommended_action || ""}
          onChange={(v) => onFilterChange("recommended_action", v)}
          options={RECOMMENDED_ACTION_OPTIONS}
        />

        <SelectField
          label="Recommendation Status"
          value={filters.recommendation_status || ""}
          onChange={(v) => onFilterChange("recommendation_status", v)}
          options={RECOMMENDATION_STATUS_OPTIONS}
        />

        <SelectField
          label="Delta vs Previous"
          value={filters.delta_direction || ""}
          onChange={(v) => onFilterChange("delta_direction", v)}
          options={[
            { value: "", label: "All deltas" },
            { value: "improved", label: "Improved only" },
            { value: "declined", label: "Declined only" },
            { value: "same", label: "No change" },
          ]}
        />
      </Box>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
        <TextField
          size="small"
          label="Search by employee name / code"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          fullWidth
          inputProps={{ "aria-label": "Search by employee name or code" }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
        />
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<RestartAltIcon />}
          onClick={onReset}
          sx={{ minWidth: 128, borderRadius: "8px", borderColor: alpha(theme.palette.text.primary, 0.28) }}
        >
          Reset
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <Paper
      sx={{
        p: { xs: 1, md: 1.15 },
        borderRadius: "8px",
        border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.primary.main, 0.14)}`,
        background: alpha(theme.palette.background.paper, 0.94),
        boxShadow: "none",
      }}
      variant="outlined"
    >
      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={1.25}
        alignItems={{ xs: "stretch", lg: "center" }}
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Stack spacing={0.2}>
          <Stack direction="row" spacing={0.8} alignItems="center">
            <TuneIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
              Report filters
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Narrow results and export data with current filter scope.
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<DownloadIcon />}
            onClick={onExportCsv}
            sx={{ borderRadius: "8px", borderColor: alpha(theme.palette.text.primary, 0.22) }}
          >
            CSV
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<FileDownloadIcon />}
            onClick={onExportExcel}
            sx={{ borderRadius: "8px", borderColor: alpha(theme.palette.text.primary, 0.22) }}
          >
            Excel
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<TaskAltIcon />}
            onClick={onFinalize}
            disabled={!canFinalize || finalizeDisabled}
            sx={{
              borderRadius: "8px",
              minWidth: 170,
              boxShadow: `0 12px 22px -14px ${alpha(theme.palette.success.main, 0.9)}`,
              background: `linear-gradient(120deg, ${theme.palette.primary.main} 0%, ${theme.palette.success.main} 100%)`,
            }}
          >
            Finalize Action {selectedCount > 0 ? `(${selectedCount})` : ""}
          </Button>
        </Stack>
      </Stack>

      {isSmall ? (
        <Accordion defaultExpanded disableGutters sx={{ borderRadius: "8px", overflow: "hidden", boxShadow: "none" }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44 }}>
            <Typography sx={{ fontWeight: 700 }}>Advanced filters</Typography>
          </AccordionSummary>
          <AccordionDetails>{filtersContent}</AccordionDetails>
        </Accordion>
      ) : (
        filtersContent
      )}
    </Paper>
  );
}
